import {
  applyFixture,
  mockDataUrl,
  MockFixture,
} from "@redotech/rpc-mock/mock-storage";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { TourBar } from "./tour-bar";
import { buildStepUrl, parseTourManifest, TourManifest } from "./tour-types";

interface TourContextValue {
  // Stable string that changes when the tour advances to a step that has
  // applied its fixture. Consumers can pass this as a `key` to force remount on
  // step transitions — needed because steps can share a route and we still want
  // fresh state + a refetch with the new fixture data.
  stepKey: string | null;
}

const DEFAULT_TOUR_CONTEXT: TourContextValue = { stepKey: null };

export const TourContext =
  createContext<TourContextValue>(DEFAULT_TOUR_CONTEXT);
TourContext.displayName = "TourContext";

/** Returns a key that changes when the tour advances; null when no tour is active. */
export function useTourStepKey(): string | null {
  return useContext(TourContext).stepKey;
}

const TOUR_PARAM = "tour";
const MOCK_PARAM = "mock";
const STEP_INDEX_KEY = "redo.dev.tour_step_index";

function getParam(name: string): string | undefined {
  return new URLSearchParams(window.location.search).get(name) || undefined;
}

function readPersistedStepIndex(): number {
  try {
    const raw = sessionStorage.getItem(STEP_INDEX_KEY);
    if (!raw) {
      return 0;
    }
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function persistStepIndex(index: number): void {
  try {
    sessionStorage.setItem(STEP_INDEX_KEY, String(index));
  } catch {
    // ignore
  }
}

function clearPersistedStepIndex(): void {
  try {
    sessionStorage.removeItem(STEP_INDEX_KEY);
  } catch {
    // ignore
  }
}

async function fetchFixture(id: string): Promise<MockFixture | undefined> {
  const res = await fetch(mockDataUrl(id), { cache: "no-store" });
  if (!res.ok) {
    console.warn(`[tour] Failed to load fixture "${id}": ${res.status}`);
    return undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (await res.json()) as MockFixture;
}

export function TourProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [tourId] = useState<string | undefined>(() =>
    typeof window === "undefined" ? undefined : getParam(TOUR_PARAM),
  );
  // Captured once so navigation can re-append it; mock mode otherwise persists
  // via localStorage, but a fresh reload of a shared link needs it in the URL.
  const [mockParam] = useState<string | undefined>(() =>
    typeof window === "undefined" ? undefined : getParam(MOCK_PARAM),
  );
  const [manifest, setManifest] = useState<TourManifest | undefined>(undefined);
  const [stepIndex, setStepIndex] = useState<number>(() =>
    typeof window === "undefined" ? 0 : readPersistedStepIndex(),
  );
  // Index whose fixture has been applied; drives the remount key. Trails
  // stepIndex while the fixture is loading so the route subtree remounts only
  // after the new fixture is in place.
  const [appliedIndex, setAppliedIndex] = useState<number>(stepIndex);
  const [loading, setLoading] = useState(false);
  const [closed, setClosed] = useState(false);
  // Step fixtures prefetched once at manifest load — keyed by fixture id. With
  // this cache, step transitions skip the round-trip and apply synchronously,
  // so next/back feels instant.
  const fixtureCacheRef = useRef<Map<string, MockFixture>>(new Map());

  // Fetch the tour manifest once we know the id.
  useEffect(() => {
    if (!tourId) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(mockDataUrl(tourId), { cache: "no-store" });
        if (!res.ok || cancelled) {
          return;
        }
        const data: unknown = await res.json();
        if (cancelled) {
          return;
        }
        const result = parseTourManifest(data);
        if (!result.ok) {
          console.warn(`[tour] "${tourId}" ${result.reason}`);
          return;
        }
        const { manifest: tour } = result;
        const startIndex = Math.min(
          readPersistedStepIndex(),
          tour.steps.length - 1,
        );
        setManifest(tour);
        setStepIndex(startIndex);
        setAppliedIndex(startIndex);

        // Prefetch every step fixture in parallel so transitions pay no
        // network cost.
        const fixtureIds = new Set<string>();
        for (const step of tour.steps) {
          if (step.fixture) {
            fixtureIds.add(step.fixture);
          }
        }
        await Promise.all(
          [...fixtureIds].map(async (id) => {
            try {
              const fixture = await fetchFixture(id);
              if (!cancelled && fixture) {
                fixtureCacheRef.current.set(id, fixture);
              }
            } catch (e) {
              console.warn("[tour] Failed to prefetch fixture", id, e);
            }
          }),
        );
      } catch (err) {
        console.warn("[tour] Failed to load manifest:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tourId]);

  // Apply step whenever stepIndex changes: navigate synchronously (so next/back
  // always advances the URL), then load the step fixture, then bump
  // appliedIndex so consumers (Outlet key) remount with the new data.
  useEffect(() => {
    if (!tourId || !manifest || closed) {
      return;
    }
    const step = manifest.steps[stepIndex];
    if (!step) {
      return;
    }
    persistStepIndex(stepIndex);
    navigate(buildStepUrl(step, { tourId, mockParam }));

    const fixtureId = step.fixture;
    if (!fixtureId) {
      setAppliedIndex(stepIndex);
      setLoading(false);
      return;
    }

    // Fast path: fixture was prefetched at manifest load. Apply synchronously,
    // invalidate, and bump appliedIndex without waiting on the network.
    const cached = fixtureCacheRef.current.get(fixtureId);
    if (cached) {
      applyFixture(cached);
      // Blanket invalidation is intentional: a step swaps the entire mock
      // dataset, and shared queries live outside the remounted route subtree.
      void queryClient.invalidateQueries();
      setAppliedIndex(stepIndex);
      setLoading(false);
      return;
    }

    // Slow path: prefetch missed. Fetch on demand, guarding against the user
    // closing or advancing the tour while the fetch is in flight.
    setLoading(true);
    let cancelled = false;
    void (async () => {
      try {
        const fixture = await fetchFixture(fixtureId);
        if (cancelled) {
          return;
        }
        if (fixture) {
          fixtureCacheRef.current.set(fixtureId, fixture);
          applyFixture(fixture);
          await queryClient.invalidateQueries();
        }
        if (!cancelled) {
          setAppliedIndex(stepIndex);
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tourId, manifest, mockParam, stepIndex, closed, queryClient, navigate]);

  const close = useCallback(() => {
    setClosed(true);
    clearPersistedStepIndex();
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete(TOUR_PARAM);
      window.history.replaceState(
        window.history.state,
        "",
        url.pathname + url.search + url.hash,
      );
    } catch {
      // ignore
    }
  }, []);

  const next = useCallback(() => {
    if (!manifest) {
      return;
    }
    // "Next" on the last step is "Done" — finish the tour rather than no-op.
    if (stepIndex >= manifest.steps.length - 1) {
      close();
      return;
    }
    setStepIndex((prev) => Math.min(prev + 1, manifest.steps.length - 1));
  }, [manifest, stepIndex, close]);

  const back = useCallback(() => {
    setStepIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const active = !!tourId && !!manifest && !closed;
  const currentStep =
    active && manifest ? manifest.steps[stepIndex] : undefined;
  const stepKey = active ? `tour-${appliedIndex}` : null;
  const contextValue = useMemo<TourContextValue>(
    () => ({ stepKey }),
    [stepKey],
  );

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      {active &&
        currentStep &&
        manifest &&
        createPortal(
          <TourBar
            back={back}
            close={close}
            loading={loading}
            next={next}
            step={currentStep}
            stepIndex={stepIndex}
            totalSteps={manifest.steps.length}
          />,
          document.body,
        )}
    </TourContext.Provider>
  );
}
